import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBoxes,
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaOilCan,
  FaPlus,
  FaRupeeSign,
  FaSearch,
  FaTimes,
  FaTrashAlt,
} from 'react-icons/fa';
import api from '../lib/api';
import './CSS/AdminPremium.css';

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === 'success'
      ? FaCheckCircle
      : notice.type === 'warning' || notice.type === 'error'
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`admin-toast ${notice.type}`}
      role={notice.type === 'warning' || notice.type === 'error' ? 'alert' : 'status'}
      aria-live={notice.type === 'warning' || notice.type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="admin-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="admin-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="admin-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm, saving }) {
  if (!open) return null;

  return (
    <div className="premium-modal-overlay" role="presentation">
      <div className="premium-modal" role="dialog" aria-modal="true" aria-labelledby="lube-modal-title">
        <div className="premium-modal-header">
          <div>
            <h5 id="lube-modal-title">{title}</h5>
            <p>{message}</p>
          </div>
          <button type="button" className="btn-close" onClick={onCancel} aria-label="Close" />
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={saving}>
            <FaTrashAlt aria-hidden="true" className="me-2" />
            {saving ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`premium-summary-card ${tone}`}>
      <div className="premium-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="premium-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

const numericSortFields = new Set(['unit_price', 'quantity_in_stock']);

function formatCurrency(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function formatQuantity(value) {
  const numeric = Number(value || 0);
  return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(2);
}

function formatDateTime(value) {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export default function AdminLubeStock() {
  const token = localStorage.getItem('token');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState(null);
  const [sortBy, setSortBy] = useState('product_name');
  const [sortDir, setSortDir] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    product_name: '',
    brand: '',
    unit_price: '',
    quantity_in_stock: '',
    unit: 'litre',
    is_active: true,
  });

  const rowsPerPage = 5;

  const showNotice = (type, title, message) => {
    setNotice({ id: Date.now(), type, title, message });
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === 'warning' || notice.type === 'error' ? 5000 : 3200
    );
    return () => clearTimeout(timer);
  }, [notice]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/lube-stock', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      showNotice('error', 'Load failed', 'Failed to load lube stock.');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({
      product_name: '',
      brand: '',
      unit_price: '',
      quantity_in_stock: '',
      unit: 'litre',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      product_name: row.product_name || '',
      brand: row.brand || '',
      unit_price: row.unit_price || '',
      quantity_in_stock: row.quantity_in_stock || '',
      unit: row.unit || 'litre',
      is_active: !!row.is_active,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      product_name: '',
      brand: '',
      unit_price: '',
      quantity_in_stock: '',
      unit: 'litre',
      is_active: true,
    });
  };

  const save = async () => {
    if (!form.product_name.trim()) {
      showNotice('warning', 'Product name required', 'Please enter a product name.');
      return;
    }
    if (!form.unit_price || Number.isNaN(Number(form.unit_price))) {
      showNotice('warning', 'Price required', 'Enter a valid unit price.');
      return;
    }
    if (form.quantity_in_stock === '' || Number.isNaN(Number(form.quantity_in_stock))) {
      showNotice('warning', 'Quantity required', 'Enter a valid opening stock quantity.');
      return;
    }

    const payload = {
      ...form,
      product_name: form.product_name.trim(),
      brand: form.brand.trim(),
      unit_price: parseFloat(form.unit_price),
      quantity_in_stock: parseFloat(form.quantity_in_stock),
    };

    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/api/lube-stock/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice('success', 'Updated', 'Lube product updated successfully.');
      } else {
        await api.post('/api/lube-stock', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice('success', 'Saved', 'Lube product added successfully.');
      }
      setShowModal(false);
      resetForm();
      await fetchItems();
    } catch (err) {
      console.error(err);
      showNotice('error', 'Save failed', err?.response?.data?.error || 'Unable to save lube stock right now.');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (row) => {
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setSaving(true);
      await api.delete(`/api/lube-stock/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotice('success', 'Deleted', 'Lube product removed successfully.');
      setDeleteTarget(null);
      await fetchItems();
    } catch (err) {
      console.error(err);
      showNotice('error', 'Delete failed', err?.response?.data?.error || 'Unable to delete this product.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(
        `/api/lube-stock/${row.id}`,
        { ...row, is_active: !row.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotice('success', 'Updated', row.is_active ? 'Product marked inactive.' : 'Product marked active.');
      fetchItems();
    } catch (err) {
      console.error(err);
      showNotice('error', 'Update failed', 'Unable to update product status.');
    }
  };

  const adjustQty = async (row, delta) => {
    const newQty = (parseFloat(row.quantity_in_stock) || 0) + delta;
    if (newQty < 0) return;

    try {
      await api.put(
        `/api/lube-stock/${row.id}`,
        { ...row, quantity_in_stock: newQty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotice('success', 'Stock updated', `Quantity adjusted by ${delta > 0 ? '+' : ''}${delta}.`);
      fetchItems();
    } catch (err) {
      console.error(err);
      showNotice('error', 'Update failed', 'Unable to adjust stock quantity.');
    }
  };

  const onSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = items.filter(
      (row) =>
        row.product_name?.toLowerCase().includes(q) || row.brand?.toLowerCase().includes(q)
    );

    rows.sort((a, b) => {
      if (numericSortFields.has(sortBy)) {
        const av = Number(a[sortBy] || 0);
        const bv = Number(b[sortBy] || 0);
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      }

      const av = (a[sortBy] ?? '').toString().toLowerCase();
      const bv = (b[sortBy] ?? '').toString().toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [items, search, sortBy, sortDir]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / rowsPerPage));
  const visibleRows = filteredSorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const stats = useMemo(() => {
    const activeCount = items.filter((item) => item.is_active).length;
    const lowStockCount = items.filter((item) => Number(item.quantity_in_stock) < 10).length;
    const totalValue = items.reduce(
      (sum, item) => sum + Number(item.quantity_in_stock || 0) * Number(item.unit_price || 0),
      0
    );
    const latestUpdatedItem = [...items]
      .filter((item) => item.updated_at || item.created_at)
      .sort((a, b) => {
        const aTime = new Date(a.updated_at || a.created_at).getTime();
        const bTime = new Date(b.updated_at || b.created_at).getTime();
        return bTime - aTime;
      })[0];

    return {
      totalCount: items.length,
      activeCount,
      lowStockCount,
      totalValue,
      latestUpdatedItem,
    };
  }, [items]);

  return (
    <div className="admin-premium-page lube-admin-page">
      <section className="premium-hero">
        <div>
          <span className="premium-eyebrow">Inventory</span>
          <h1>Lube Stock</h1>
          <p>Track lube inventory, pricing, recent stock touches, and product status in a layout that stays usable on smaller screens too.</p>
        </div>
        <div className="premium-tab-strip">
          <button className="btn btn-primary" onClick={openNew}>
            <FaPlus aria-hidden="true" className="me-2" />
            Add Product
          </button>
        </div>
      </section>

      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="premium-summary-grid">
        <StatCard tone="blue" icon={FaBoxes} label="Total Products" value={stats.totalCount} caption="Products tracked in lube stock" />
        <StatCard tone="green" icon={FaOilCan} label="Active" value={stats.activeCount} caption="Currently active products" />
        <StatCard tone="amber" icon={FaExclamationCircle} label="Low Stock" value={stats.lowStockCount} caption="Needs attention soon" />
        <StatCard tone="red" icon={FaRupeeSign} label="Stock Value" value={formatCurrency(stats.totalValue)} caption="Estimated inventory value" />
        <StatCard
          tone="blue"
          icon={FaInfoCircle}
          label="Last Stock Update"
          value={stats.latestUpdatedItem?.product_name || '-'}
          caption={
            stats.latestUpdatedItem
              ? `${formatQuantity(stats.latestUpdatedItem.quantity_in_stock)} ${stats.latestUpdatedItem.unit || 'unit'} in stock`
              : 'No stock updates yet'
          }
        />
        <StatCard
          tone="green"
          icon={FaCheckCircle}
          label="Updated At"
          value={formatDateTime(stats.latestUpdatedItem?.updated_at || stats.latestUpdatedItem?.created_at)}
          caption="Most recent stock touch across products"
        />
      </section>

      <div className="premium-context-strip lube-stock-toolbar">
        <div className="flex-grow-1">
          <label className="form-label">Search</label>
          <div className="input-group">
            <span className="input-group-text">
              <FaSearch aria-hidden="true" />
            </span>
            <input
              className="form-control"
              placeholder="Search by name or brand"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="premium-context-chip">
          <span>Sort</span>
          <strong>{sortBy.replace(/_/g, ' ')}</strong>
          <small>{sortDir === 'asc' ? 'Ascending' : 'Descending'}</small>
        </div>
      </div>

      <div className="table-responsive premium-table-shell lube-stock-table-shell">
        <table className="table table-hover align-middle lube-stock-table">
          <thead>
            <tr>
              <th onClick={() => onSort('product_name')} className="sortable">Product</th>
              <th onClick={() => onSort('brand')} className="sortable">Brand</th>
              <th>Unit</th>
              <th onClick={() => onSort('unit_price')} className="sortable">Price</th>
              <th onClick={() => onSort('quantity_in_stock')} className="sortable">Stock</th>
              <th>Updated At</th>
              <th>Status</th>
              <th style={{ minWidth: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="8" className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && visibleRows.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center text-muted">
                  No products found
                </td>
              </tr>
            )}
            {!loading &&
              visibleRows.map((row) => (
                <tr key={row.id} className={Number(row.quantity_in_stock) < 10 ? 'low-stock' : ''}>
                  <td data-label="Product">
                    <strong>{row.product_name}</strong>
                  </td>
                  <td data-label="Brand">{row.brand || '-'}</td>
                  <td data-label="Unit">{row.unit}</td>
                  <td data-label="Price">{formatCurrency(row.unit_price)}</td>
                  <td data-label="Stock">
                    <div className="d-flex align-items-center gap-1 stock-adjust-controls">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => adjustQty(row, -1)}>
                        -
                      </button>
                      <strong>{formatQuantity(row.quantity_in_stock)}</strong>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => adjustQty(row, +1)}>
                        +
                      </button>
                    </div>
                  </td>
                  <td data-label="Updated At">{formatDateTime(row.updated_at || row.created_at)}</td>
                  <td data-label="Status">
                    <span className={`badge ${row.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {row.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label="Actions">
                    <div className="d-flex flex-wrap gap-1 stock-action-group">
                      <button className="btn btn-sm btn-warning" onClick={() => openEdit(row)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-outline-info" onClick={() => toggleActive(row)}>
                        {row.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => requestDelete(row)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div className="premium-pagination">
          <div className="page-nav">
            <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>
              Prev
            </button>
          </div>
          <div className="page-chips">
            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
              <button
                key={page}
                type="button"
                className={page === currentPage ? 'active' : ''}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
          </div>
          <div className="page-nav">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="premium-modal-overlay">
          <div className="premium-modal">
            <div className="premium-modal-header">
              <div>
                <h5>{editingId ? 'Edit Product' : 'Add Product'}</h5>
                <p>Update pricing, stock, and activation state from one focused form.</p>
              </div>
              <button className="btn-close" onClick={() => setShowModal(false)} aria-label="Close" />
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Product Name</label>
                <input
                  className="form-control"
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Brand</label>
                <input
                  className="form-control"
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Unit</label>
                <select
                  className="form-select"
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                >
                  <option value="litre">Litre</option>
                  <option value="ml">ml</option>
                  <option value="piece">Piece</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Unit Price (Rs)</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Opening Stock</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.quantity_in_stock}
                  onChange={(e) => setForm({ ...form, quantity_in_stock: e.target.value })}
                />
              </div>
              <div className="col-md-4 d-flex align-items-center">
                <div className="form-check mt-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="lubeActiveChk"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="lubeActiveChk">
                    Active
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete product?"
        message={`Remove ${deleteTarget?.product_name || 'this product'} from the lube stock registry.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        saving={saving}
      />
    </div>
  );
}
